import { debounce } from "lodash";
import * as React from "react";
import { ComponentType } from "react";
import { connect } from "react-redux";
import { actions, Form } from "react-redux-form";
import { routerActions } from "react-router-redux";
import Alert from "react-s-alert";
import { bindActionCreators } from "redux";
import {
    CommitteeDetailsBreadcrumbs,
    CommitteesCreateBreadcrumbs,
} from "src/helpers/breadcrumbHelper";
import RouterHoc, { IRouterActions, IRouterProps } from "src/hoc/RouterHoc";
import * as CommitteeActions from "../../../actions/committees";
import * as NavigationActions from "../../../actions/navigation";
import { NotificationAlert } from "../../../components/common/index";
import {
    CheckboxFormInput,
    NumberFormInput,
    TextFormInput,
} from "../../../components/form/index";
import { RestrictedLayout } from "../../../components/layouts/index";
import { FontAwesomeIcons } from "../../../constants/fontAwesomeIcons";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import {
    renderRegistrationFormSaveBlock,
    renderRegistrationFormSaveSuccess,
    renderRegistrationFormSaveWarning,
} from "../../../helpers/alert";
import * as AuthenticationHelper from "../../../helpers/authentication";
import {
    RegistrationComponent,
    renderFormControls,
} from "../../../helpers/formHelper";
import { CommitteeSubMenu } from "../../../helpers/subMenuHelper";
import { RegistrationFormComponent } from "../../../interfaces/forms/IRegistrationComponent";
import { ITmdState } from "../../../reducers/index";
import {
    ICommitteeReduxFormState,
    initialCommitteeState,
} from "../../../reducers/reactReduxForms/committee";
import { IRegistrationFormState } from "../../../reducers/reactReduxForms/index";

import "./CommitteePage.scss";

interface ICommitteePageParams {
    committeeId: number;
}

interface ICommitteePageProps extends IRouterProps<ICommitteePageParams> {
    form: Dtos.Committee & IRegistrationFormState;
    formState: Dtos.FormState;
    formProperties: Dtos.FormProperty[];
    reduxFormState: ICommitteeReduxFormState;

    committeeId: number;

    // From state
    committee: Dtos.Committee;
    lookups: Dtos.Lookup[];

    loadingCommittee: boolean;
    loadCommitteeSuccess: boolean;
    loadCommitteeFailure: boolean;
    loadCommitteeErrorCode: string;

    savingCommittee: boolean;
    saveCommitteeSuccess: boolean;
    saveCommitteeFailure: boolean;

    creatingCommittee: boolean;
    createCommitteeSuccess: boolean;
    createCommitteeFailure: boolean;

    validatingCommittee: boolean;

    validationFailures: Dtos.ResponseError[];

    user: Dtos.User;
    permissions?: Dtos.PersonnelTmdPermissions[];
}

interface ICommitteePageActions extends IRouterActions {
    clearCommittee: CommitteeActions.ICommitteeClearActionCreator;
    loadCommittee: CommitteeActions.ICommitteeLoadByIdActionCreator;

    saveCommittee: CommitteeActions.ICommitteeSaveActionCreator;
    createCommittee: CommitteeActions.ICommitteeCreateActionCreator;
    validateCommittee: CommitteeActions.ICommitteeValidateActionCreator;

    loadForm: typeof actions.load;
    changeForm: typeof actions.change;
    resetForm: typeof actions.reset;

    navigate: typeof routerActions.push;
    navigateReplace: typeof routerActions.replace;

    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;

}

type CommitteePageProps = ICommitteePageProps & ICommitteePageActions;

const reduxFormName: string = "reduxForms.committee";

class CommitteePage extends React.PureComponent<CommitteePageProps, any> {
    initialised: boolean;
    hasBeenSubmitted: boolean;

    constructor(props: CommitteePageProps) {
        super(props);

        this.state = {
            initialised: false,
            hasBeenSubmitted: false,
        };

        this.clearCommittee = this.clearCommittee.bind(this);
        this.onCancel = this.onCancel.bind(this);
        this.loadForm = this.loadForm.bind(this);
        this.debounceValidate = this.debounceValidate.bind(this);
    }

    componentDidMount() {
        const {
            loadCommittee,
            committeeId,
            createCommittee,
            permissions,
            setDrawerItems,
            setBreadcrumbs
        } = this.props;
        

        if (committeeId) {
            setDrawerItems(CommitteeSubMenu(committeeId, permissions));
            setBreadcrumbs(CommitteeDetailsBreadcrumbs(committeeId));
            loadCommittee(committeeId);
        } else if (!committeeId) {
            setBreadcrumbs(CommitteesCreateBreadcrumbs());
            createCommittee();
        }
    }

    componentDidUpdate(prevProps: CommitteePageProps, _prevState) {
        const {
            committee,
            committeeId,
            form,
            navigate,
            loadCommittee,
            loadingCommittee,
            loadCommitteeSuccess,
            createCommittee,
            creatingCommittee,
            createCommitteeSuccess,
            savingCommittee,
            saveCommitteeSuccess,
            validationFailures,
            setDrawerItems,
            setBreadcrumbs,
            permissions,
            loadCommitteeFailure,
            loadCommitteeErrorCode,
        } = this.props;

        if (loadCommitteeFailure) {
            navigate(`/error/${loadCommitteeErrorCode}`);
        }

        if (committeeId && committee?.name) {
            setDrawerItems(CommitteeSubMenu(committeeId, permissions));
            setBreadcrumbs(
                CommitteeDetailsBreadcrumbs(committeeId, committee.name)
            );
        }

        if (committeeId && prevProps.committeeId != committeeId) {
            loadCommittee(committeeId);
        } else if (!committeeId && prevProps.committeeId) {
            createCommittee();
        }

        if (!loadingCommittee && prevProps.loadingCommittee) {
            if (loadCommitteeSuccess) {
                this.loadForm();
            }
        }

        if (form != prevProps.form) {
            this.debounceValidate();
        }

        if (!savingCommittee && prevProps.savingCommittee) {
            if (saveCommitteeSuccess) {
                if (validationFailures && validationFailures.length > 0) {
                    Alert.warning(
                        <NotificationAlert
                            alertContent={renderRegistrationFormSaveWarning(
                                validationFailures
                            )}
                            icon={FontAwesomeIcons.Solid.EXCLAMATION_CIRCLE}
                        />
                    );
                } else {
                    Alert.success(
                        <NotificationAlert
                            alertContent={renderRegistrationFormSaveSuccess()}
                            icon={FontAwesomeIcons.Solid.CHECK}
                        />
                    );
                }
                if (form.saveAndReturn) {
                    navigate("/committees");
                } else if (
                    !form.saveAndReturn &&
                    committeeId &&
                    committeeId > 0
                ) {
                    this.loadForm();
                } else if (
                    !form.saveAndReturn &&
                    !committeeId &&
                    committee &&
                    committee.id > 0
                ) {
                    navigate("/committees/" + committee.id + "/details");
                }
            } else {
                Alert.error(
                    <NotificationAlert
                        alertContent={this.renderErrorAlert()}
                        icon={FontAwesomeIcons.Solid.TIMES_OCTAGON}
                    />
                );
            }
        }

        if (!creatingCommittee && prevProps.creatingCommittee) {
            if (createCommitteeSuccess) {
                this.loadForm();
            }
        }
    }

    componentWillUnmount() {
        const { resetForm, loadForm } = this.props;

        this.clearCommittee();
        loadForm(reduxFormName, initialCommitteeState);
        resetForm(reduxFormName);
    }

    clearCommittee() {
        this.props.clearCommittee();
    }

    showCommittee(): boolean {
        const { loadingCommittee, loadCommitteeSuccess } = this.props;

        if (!loadingCommittee && loadCommitteeSuccess) {
            return true;
        }

        return false;
    }

    debounceValidate = debounce(async () => {
        const hasBeenSubmitted = this.state?.hasBeenSubmitted;
        const { form, validatingCommittee, validateCommittee } = this.props;

        let changes: Dtos.Committee = {} as Dtos.Committee;

        if (form && hasBeenSubmitted && !validatingCommittee) {
            await validateCommittee(Object.assign({ ...form }, changes));
        }
    }, 250);

    render() {
        const { committeeId, committee, loadingCommittee, permissions } =
            this.props;

        return (
            <RestrictedLayout
                permission={Dtos.Permission.CommitteeView}
                subMenuTitle={committee ? committee.name : ""}
                subMenuItems={CommitteeSubMenu(committeeId, permissions)}
                loading={loadingCommittee}
            >
                {this.renderContent()}
            </RestrictedLayout>
        );
    }

    renderContent() {
        const {
            form,
            lookups,
            validationFailures,
            formProperties,
            formState,
            changeForm,
            savingCommittee,
            permissions,
            committeeId,
        } = this.props;

        const hasPermission: boolean =
            AuthenticationHelper.hasCreateEditPermissionFor(
                undefined,
                committeeId,
                permissions,
                Dtos.Permission.CommitteeUpdate,
                Dtos.Permission.CommitteeCreate
            );
        const formDisabled: boolean =
            !hasPermission ||
            savingCommittee ||
            (formState && formState.formStatus == Dtos.FormStatus.Disabled);

        let formComponents: { [index: string]: RegistrationFormComponent } = {
            detailsCommittee: {
                type: RegistrationComponent.RegistrationElement,
                component: (
                    <>
                        <h1>Details</h1>
                        <div className="card card-shadow" style={{ zIndex: 1 }}>
                            <div className="form-row pt-4 pb-2 px-4">
                                <div className="col-md-12">
                                    {renderFormControls(
                                        form,
                                        "committee",
                                        {
                                            name: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: TextFormInput,
                                                inputDisabled: formDisabled,
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-2",
                                                fieldClassName:
                                                    "col-sm-10 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row py-2 px-4">
                                <div className="col-md-12">
                                    {renderFormControls(
                                        form,
                                        "committee",
                                        {
                                            rank: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: NumberFormInput,
                                                inputDisabled: formDisabled,
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-2",
                                                fieldClassName:
                                                    "col-sm-10 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row pt-2 pb-4 px-4">
                                <div className="col-md-4">
                                    {renderFormControls(
                                        form,
                                        "committee",
                                        {
                                            active: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: CheckboxFormInput,
                                                inputDisabled: formDisabled,
                                                inputProps: {
                                                    label: "Active",
                                                },
                                                inputLabel: " ",
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-2",
                                                labelClassName:
                                                    "col-form-label col-sm-6",
                                                fieldClassName:
                                                    "col-sm-6 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                                <div className="col-md-4">
                                    {renderFormControls(
                                        form,
                                        "committee",
                                        {
                                            adminOnly: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: CheckboxFormInput,
                                                inputDisabled: formDisabled,
                                                inputProps: {
                                                    label: "Admin Only",
                                                },
                                                inputLabel: " ",
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-2",
                                                labelClassName:
                                                    "col-form-label col-sm-6",
                                                fieldClassName:
                                                    "col-sm-6 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                                <div className="col-md-4">
                                    {renderFormControls(
                                        form,
                                        "committee",
                                        {
                                            tmdAdminCanEdit: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: CheckboxFormInput,
                                                inputDisabled: formDisabled,
                                                inputProps: {
                                                    label: "TMD Admin Can Edit",
                                                },
                                                inputLabel: " ",
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-2",
                                                labelClassName:
                                                    "col-form-label col-sm-6",
                                                fieldClassName:
                                                    "col-sm-6 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                        </div>
                    </>
                ),
            },
        };

        return (
            <Form
                model={reduxFormName}
                onSubmit={(val, _event) => this.handleOnSubmit(val)}
                onSubmitFailed={(val) => this.handleOnSubmitFailed(val)}
            >
                {renderFormControls(
                    form,
                    "committee",
                    formComponents,
                    lookups,
                    validationFailures,
                    formProperties,
                    changeForm
                )}
                <div className="mt-4 mb-4 text-right">
                    <button
                        disabled={formDisabled}
                        onClick={() => {
                            this.props.changeForm(
                                reduxFormName + ".saveAndReturn",
                                false
                            );
                        }}
                        type="submit"
                        className="btn btn-primary mr-2"
                    >
                        Submit
                    </button>

                    <button
                        disabled={formDisabled}
                        onClick={() => {
                            this.props.changeForm(
                                reduxFormName + ".saveAndReturn",
                                true
                            );
                        }}
                        type="submit"
                        className="btn btn-primary mr-2"
                    >
                        Submit And Return
                    </button>
                    <button
                        onClick={this.onCancel}
                        className="btn btn-primary mr-2"
                    >
                        {" "}
                        Cancel{" "}
                    </button>
                </div>
            </Form>
        );
    }

    renderErrorAlert(): any {
        return (
            <div>
                The form was <strong> not </strong> saved.
            </div>
        );
    }

    renderWarningAlert(): any {
        const { validationFailures } = this.props;

        return (
            <div>
                The form was saved but was invalid with{" "}
                {validationFailures.length} errors.
            </div>
        );
    }

    renderSaveSuccessAlert(): any {
        return <div>The form has been saved successfully</div>;
    }

    handleOnSubmit(data: Dtos.Committee) {
        const { hasBeenSubmitted } = this.state;
        const { saveCommittee } = this.props;

        if (!hasBeenSubmitted) {
            this.setState({ hasBeenSubmitted: true });
        }

        if (data) {
            let changes: Dtos.Committee = {} as Dtos.Committee;
            saveCommittee(Object.assign({ ...data }, changes));
        }
    }

    handleOnSubmitFailed(_data: Dtos.Committee) {
        Alert.error(
            <NotificationAlert
                alertContent={renderRegistrationFormSaveBlock()}
                icon={FontAwesomeIcons.Solid.BAN}
            />
        );
    }

    onCancel() {
        const { navigate } = this.props;

        navigate("/committees");
    }

    loadForm() {
        const { loadForm, committee } = this.props;

        let extraData: Dtos.Committee = {} as Dtos.Committee;

        loadForm(reduxFormName, Object.assign({ ...committee }, extraData));
    }
}

const mapStateToProps = (
    state: ITmdState,
    ownProps: CommitteePageProps
): ICommitteePageProps => {
    let committee: Dtos.Committee | undefined = !(
        state.committees.formData instanceof Array
    )
        ? state.committees.formData
        : undefined;
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];

    return {
        match: ownProps.match,
        committeeId:
            ownProps.match &&
            ownProps.match.params.committeeId?.toString() != "create"
                ? ownProps.match.params.committeeId
                : undefined!,

        history: ownProps.history,
        location: ownProps.location,

        committee: committee!,

        loadingCommittee:
            state.committees.loadState &&
            state.committees.loadState.status == RequestState.Pending,
        loadCommitteeSuccess:
            state.committees.loadState &&
            state.committees.loadState.status == RequestState.Success,
        loadCommitteeFailure:
            state.committees.loadState &&
            state.committees.loadState.status == RequestState.Failure,
        loadCommitteeErrorCode: state.committees.loadState.errorCode,

        savingCommittee:
            state.committees.saveState &&
            state.committees.saveState.status == RequestState.Pending,
        saveCommitteeSuccess:
            state.committees.saveState &&
            state.committees.saveState.status == RequestState.Success,
        saveCommitteeFailure:
            state.committees.saveState &&
            state.committees.saveState.status == RequestState.Failure,

        creatingCommittee:
            state.committees.createState &&
            state.committees.createState.status == RequestState.Pending,
        createCommitteeSuccess:
            state.committees.createState &&
            state.committees.createState.status == RequestState.Success,
        createCommitteeFailure:
            state.committees.createState &&
            state.committees.createState.status == RequestState.Failure,

        validatingCommittee:
            state.committees.validateState &&
            state.committees.validateState.status == RequestState.Pending,

        user: state.user.data,
        permissions: permissions,
        lookups: state.committees.lookups,

        form: state.reduxForms.committee,
        formState: state.committees.formState,
        formProperties: state.committees.formProperties,

        reduxFormState: state.reduxForms.formState.committee,
        validationFailures: committee
            ? state.committees.validationFailures
            : undefined,
    };
};

const mapDispatchToProps = (
    dispatch,
    ownProps: CommitteePageProps
): ICommitteePageActions => {
    return {
        navigate: ownProps.navigate,
        navigateReplace: ownProps.navigateReplace,

        loadForm: bindActionCreators(actions.load, dispatch),
        changeForm: bindActionCreators(actions.change, dispatch),
        resetForm: bindActionCreators(actions.reset, dispatch),

        loadCommittee: bindActionCreators(
            CommitteeActions.LoadCommitteeById,
            dispatch
        ),
        clearCommittee: bindActionCreators(CommitteeActions.Clear, dispatch),

        saveCommittee: bindActionCreators(
            CommitteeActions.SaveCommittee,
            dispatch
        ),
        createCommittee: bindActionCreators(
            CommitteeActions.CreateCommittee,
            dispatch
        ),
        validateCommittee: bindActionCreators(
            CommitteeActions.ValidateCommittee,
            dispatch
        ),

        setBreadcrumbs: bindActionCreators(
            NavigationActions.SetBreadcrumbs,
            dispatch
        ),
        setDrawerItems: bindActionCreators(
            NavigationActions.SetDrawerItems,
            dispatch
        )
    };
};

export default RouterHoc<ICommitteePageParams, CommitteePageProps>(
    connect(
        mapStateToProps,
        mapDispatchToProps
    )(CommitteePage) as unknown as ComponentType<CommitteePageProps>
) as ComponentType<any>;
